<script setup>
import { ref, computed, onMounted } from 'vue';
import { useAlert } from 'dashboard/composables';
import BitrixStagesAPI from 'dashboard/api/bitrixStages';

const stages = ref([]);
const loading = ref(false);
const syncing = ref(false);
const filter = ref('all'); // all | lead | deal

const fetchStages = async () => {
  loading.value = true;
  try {
    const { data } = await BitrixStagesAPI.get();
    stages.value = data;
  } catch (error) {
    useAlert('Не удалось загрузить стадии');
  } finally {
    loading.value = false;
  }
};
onMounted(fetchStages);

const syncNow = async () => {
  syncing.value = true;
  try {
    await BitrixStagesAPI.sync();
    await fetchStages();
    useAlert('Стадии обновлены из Bitrix24');
  } catch (error) {
    useAlert('Ошибка синхронизации');
  } finally {
    syncing.value = false;
  }
};

const filtered = computed(() =>
  filter.value === 'all'
    ? stages.value
    : stages.value.filter(s => s.stage_type === filter.value)
);

// single flat list: leads first, then by pipeline, then by stage SORT
const rows = computed(() =>
  [...filtered.value].sort((a, b) => {
    if (a.stage_type !== b.stage_type) return a.stage_type === 'lead' ? -1 : 1;
    if (a.pipeline_name !== b.pipeline_name) {
      return a.pipeline_name.localeCompare(b.pipeline_name);
    }
    return a.sort - b.sort;
  })
);

const semanticsLabel = s => {
  if (s === 'success') return 'успех';
  if (s === 'failure') return 'провал';
  if (s === 'process') return 'в работе';
  return s || '';
};
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-auto bg-n-surface-1">
    <div class="w-full max-w-6xl px-6 py-6 mx-auto">
      <div class="flex items-start justify-between gap-4 mb-4">
        <div>
          <h1 class="mb-1 text-xl font-medium text-n-slate-12">Стадии</h1>
          <p class="text-sm text-n-slate-11">
            Справочник стадий из Bitrix24 — лиды и сделки, сгруппированы по воронкам.
            Обновляется при синхронизации Bitrix24 (или кнопкой).
          </p>
        </div>
        <button
          type="button"
          class="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-n-weak text-n-slate-11 hover:bg-n-alpha-1 shrink-0 disabled:opacity-50"
          :disabled="syncing"
          @click="syncNow"
        >
          <span class="i-lucide-refresh-cw size-4" :class="{ 'animate-spin': syncing }" />
          {{ syncing ? 'Обновление…' : 'Обновить из Bitrix24' }}
        </button>
      </div>

      <div class="flex items-center gap-1 mb-4">
        <button
          v-for="f in [
            ['all', 'Все'],
            ['lead', 'Лиды'],
            ['deal', 'Сделки'],
          ]"
          :key="f[0]"
          type="button"
          class="px-3 py-1 text-sm rounded-lg border border-n-weak transition-colors"
          :class="
            filter === f[0]
              ? 'bg-n-slate-3 text-n-slate-12 dark:bg-n-solid-3'
              : 'text-n-slate-11 hover:bg-n-alpha-1'
          "
          @click="filter = f[0]"
        >
          {{ f[1] }}
        </button>
      </div>

      <div v-if="loading" class="py-10 text-center text-n-slate-11">Загрузка…</div>
      <div
        v-else-if="!stages.length"
        class="py-10 text-center text-n-slate-11"
      >
        Стадий нет. Нажмите «Обновить из Bitrix24».
      </div>

      <div
        v-else
        class="overflow-x-auto border rounded-xl border-n-weak bg-n-solid-1"
      >
        <table class="w-full text-sm border-collapse">
          <thead>
            <tr class="border-b border-n-weak">
              <th class="px-3 py-2 font-medium text-left text-n-slate-11 whitespace-nowrap">Воронка</th>
              <th class="px-3 py-2 font-medium text-left text-n-slate-11 whitespace-nowrap">Тип</th>
              <th class="px-3 py-2 font-medium text-left text-n-slate-11 whitespace-nowrap">Стадия</th>
              <th class="px-3 py-2 font-medium text-left text-n-slate-11 whitespace-nowrap">STATUS_ID</th>
              <th class="px-3 py-2 font-medium text-left text-n-slate-11 whitespace-nowrap">Семантика</th>
              <th class="px-3 py-2 font-medium text-left text-n-slate-11 whitespace-nowrap">Системная</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="s in rows"
              :key="s.id"
              class="border-b border-n-weak last:border-0 hover:bg-n-alpha-1"
            >
              <td class="px-3 py-2 font-medium text-n-slate-12 whitespace-nowrap">
                {{ s.pipeline_name }}
              </td>
              <td class="px-3 py-2 whitespace-nowrap">
                <span
                  class="text-xs px-1.5 py-0.5 rounded"
                  :class="
                    s.stage_type === 'lead'
                      ? 'bg-n-amber-3 text-n-amber-11'
                      : 'bg-n-blue-3 text-n-blue-11'
                  "
                >{{ s.stage_type === 'lead' ? 'Лид' : 'Сделка' }}</span>
              </td>
              <td class="px-3 py-2 text-n-slate-12 whitespace-nowrap">
                <span class="inline-flex items-center gap-2">
                  <span
                    class="inline-block rounded size-3 shrink-0"
                    :style="{ backgroundColor: s.color || '#cbd5e1' }"
                  />
                  {{ s.name }}
                </span>
              </td>
              <td class="px-3 py-2 font-mono text-xs text-n-slate-11 whitespace-nowrap">
                {{ s.status_id }}
              </td>
              <td class="px-3 py-2 text-n-slate-11 whitespace-nowrap">
                {{ semanticsLabel(s.semantics) || '—' }}
              </td>
              <td class="px-3 py-2 text-n-slate-11 whitespace-nowrap">
                {{ s.system ? 'да' : 'нет' }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>
